/**
 * Pure resolver for a range-kind reset-zoom rule: given the loaded bars and a
 * rule, the `[from, to]` the chart should frame after a reset. `to` is always
 * the latest loaded bar; the right edge never moves (spec D5).
 *
 * The engine (`ChartManager.resetTimeScale`) hands the result to
 * `setVisibleDateRange`, whose first-match semantics handle a `from` earlier
 * than the buffer (index 0, everything loaded). This module never clamps
 * `from` to `bars[0]` for that reason: the engine owns that degradation.
 *
 * Spec: docs/superpowers/specs/2026-09-22-chart-reset-zoom-defaults-design.md
 * (D5 anchors, D6 range modes, "Engine design").
 */

import type { IntradayAnchor, ResetZoomRangeRule } from '../types/resetZoom'
import { computeAnchorWindow, deriveSessionDateKeys } from './anchorWindow'
import { DEFAULT_SESSION_TZ, lastSessionDaysStart, timestampsOf } from './sessionDay'

/**
 * Start bound per intraday anchor, as an ET minute of day plus a session
 * offset (spec D5). Same vocabulary as `AnchorVisibleWindow`: 570 = 9:30,
 * 240 = 4:00, 960 = 16:00; -1 = the prior loaded trading session.
 */
export const INTRADAY_ANCHOR_START: Record<
  IntradayAnchor,
  { startMin: number; startDayOffset: number }
> = {
  open: { startMin: 570, startDayOffset: 0 },
  premarket: { startMin: 240, startDayOffset: 0 },
  yesterdayAfterHours: { startMin: 960, startDayOffset: -1 },
}

/** Timestamp of the bar `n` from the end (clamped to the buffer). */
function timestampFromEnd(bars: ReadonlyArray<{ timestamp: number }>, n: number): number {
  const idx = Math.min(bars.length - 1, Math.max(0, bars.length - n))
  return bars[idx].timestamp
}

/**
 * `[from, to]` for a range-kind rule against the loaded bars, or null when
 * there are no bars. `to` is always the latest bar.
 *
 * - `anchor`: the anchor's instant on the latest bar's ET session (walking
 *   back loaded sessions for a negative day offset), or `minBars` back,
 *   whichever is earlier. An anchor later than the latest bar (pre-open
 *   Monday) therefore falls to the floor with no special case.
 *   `resolveAnchorVisibleRange` is deliberately not used: its "full event
 *   day" fallback is a history-mode semantic (spec D5).
 * - `daysBack`: the first bar of the `days` most recent trading sessions,
 *   counted from the loaded bars so weekends and holidays are skipped.
 * - `barsBack`: an index walk.
 *
 * `tz` reaches only the `daysBack` session bucketing. The anchor path is
 * hard-wired to ET by `computeAnchorWindow` / `deriveSessionDateKeys`, the
 * same as every other anchored-window caller.
 */
export function resolveResetRange(
  bars: ReadonlyArray<{ timestamp: number }>,
  rule: ResetZoomRangeRule,
  tz: string = DEFAULT_SESSION_TZ,
): { from: number; to: number } | null {
  const len = bars.length
  if (len === 0) return null
  const to = bars[len - 1].timestamp

  switch (rule.kind) {
    case 'anchor': {
      const { startMin, startDayOffset } = INTRADAY_ANCHOR_START[rule.anchor]
      const anchored = computeAnchorWindow(
        to,
        { startMin, endMin: startMin, startDayOffset },
        deriveSessionDateKeys(bars),
      ).from
      const floorFrom = timestampFromEnd(bars, rule.minBars)
      return { from: Math.min(anchored, floorFrom), to }
    }
    case 'daysBack': {
      const startIdx = lastSessionDaysStart(timestampsOf(bars), rule.days, tz)
      return { from: bars[Math.min(len - 1, startIdx)].timestamp, to }
    }
    case 'barsBack':
      return { from: timestampFromEnd(bars, rule.bars), to }
  }
}
